import { Stop, TransportType } from 'types/QuoteRequest';
import { Maybe, Undef } from 'types/UtilityTypes';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';
import { normalizeLtlFreightClassSuggestion } from 'utils/densityToFreightClass';

import { QuoteCountries, QuoteFormStop } from '../../types';
import {
  getSuggestedCommodities,
  mapSuggestedAccessorialsToLTLSelected,
  mapSuggestedCommoditiesToLTLItems,
} from '../../utils/mapSuggestedCommoditiesToLTLItems';
import {
  TaiLtlPickupHours,
  resolveTaiLtlPickupWindow,
} from '../../utils/resolveTaiLtlPickupWindow';
import { BatchLTLShipmentInput } from '../types';
import { sortBatchLTLSuggestionsBySourceOrder } from './getBatchLTLSuggestions';

// Form defaults for every shipment, in the order the shipper listed them.
export function buildBatchLTLDefaultValues(
  suggestions: SuggestedQuoteChange[],
  now: Date = new Date(),
  pickupHours?: Maybe<TaiLtlPickupHours>
): BatchLTLShipmentInput[] {
  return sortByEmailOrder(suggestions).map((suggestion, index) =>
    buildShipmentDefaults(suggestion, index, now, pickupHours)
  );
}

type EmptyShipmentParams = {
  key: number;
  label: string;
  // The shipment whose origin and pickup window the new one starts from.
  template: Undef<BatchLTLShipmentInput>;
  isOriginShared: boolean;
  now?: Date;
  pickupHours?: Maybe<TaiLtlPickupHours>;
};

// A shipment the rep adds by hand: the shared origin and the batch's pickup window, nothing
// else. It has no quote request until it is rated.
export function buildEmptyBatchLTLShipment({
  key,
  label,
  template,
  isOriginShared,
  now = new Date(),
  pickupHours,
}: EmptyShipmentParams): BatchLTLShipmentInput {
  const { pickupDate, pickupCloseDate } =
    template ?? resolveTaiLtlPickupWindow(null, null, now, pickupHours);
  const origin =
    isOriginShared && template?.stops[0]
      ? { ...template.stops[0], order: 0 }
      : toFormStop(0, undefined);

  return {
    key,
    quoteRequestId: 0,
    label,
    notes: '',
    customerName: template?.customerName ?? '',
    transportType: TransportType.VAN,
    pickupDate,
    pickupCloseDate,
    deliveryDate: toDate(null) as Date,
    deliveryCloseDate: null,
    stops: [origin, toFormStop(1, undefined)],
    ltl: {
      commodity: {
        freightClass: '',
        nmfcCode: '',
        stackable: false,
        sideLoadEnabled: false,
        allowSplitMerge: false,
      },
      items: [],
      selectedAccessorials: [],
      pickupServices: [],
      deliveryServices: [],
      compatibilityProfiles: [],
    },
    isSubmitToTMS: false,
    customAccessorials: [],
  };
}

// Extraction order is the fallback when a shipment has no position from the email.
function sortByEmailOrder(
  suggestions: SuggestedQuoteChange[]
): SuggestedQuoteChange[] {
  return sortBatchLTLSuggestionsBySourceOrder(suggestions);
}

function buildShipmentDefaults(
  suggestion: SuggestedQuoteChange,
  index: number,
  now: Date,
  pickupHours: Maybe<TaiLtlPickupHours> | undefined
): BatchLTLShipmentInput {
  const suggested = suggestion.suggested;
  const commodities = getSuggestedCommodities(suggested);
  const items = mapSuggestedCommoditiesToLTLItems(commodities, {
    shouldNormalizeFreightClass: true,
    isThreeGActive: false,
  });
  const { pickupDate, pickupCloseDate } = resolveTaiLtlPickupWindow(
    toDate(suggested?.pickupDate),
    null,
    now,
    pickupHours
  );

  const batchShipment = suggested?.batchLtlShipment;

  return {
    key: suggestion.id,
    quoteRequestId: suggestion.id,
    label:
      batchShipment?.label?.trim() ||
      commodities[0]?.description?.trim() ||
      `Shipment ${index + 1}`,
    notes: batchShipment?.notes?.trim() ?? '',
    customerName: suggested?.customerExternalTMSID ?? '',
    transportType: TransportType.VAN,
    pickupDate,
    pickupCloseDate,
    deliveryDate: toDate(suggested?.deliveryDate) as Date,
    deliveryCloseDate: null,
    stops: getShipmentStops(suggestion),
    ltl: {
      commodity: {
        freightClass: normalizeLtlFreightClassSuggestion(
          commodities[0]?.freightClass
        ),
        nmfcCode: commodities[0]?.nmfc ?? '',
        stackable: false,
        sideLoadEnabled: false,
        allowSplitMerge: false,
      },
      items,
      selectedAccessorials: mapSuggestedAccessorialsToLTLSelected(suggested),
      pickupServices: [],
      deliveryServices: [],
      compatibilityProfiles: [],
    },
    isSubmitToTMS: false,
    customAccessorials: [],
  };
}

function getShipmentStops(suggestion: SuggestedQuoteChange): QuoteFormStop[] {
  const suggested = suggestion.suggested;
  const stops = asArrayOrObjectValues(
    suggested?.stops as Undef<Stop[] | Record<string, Stop>>
  );

  if (stops.length > 0) {
    const pickup = stops.find((stop) => stop.stopType === 'pickup') ?? stops[0];
    const delivery =
      [...stops].reverse().find((stop) => stop.stopType === 'dropoff') ??
      stops[stops.length - 1];

    return [
      toFormStop(0, pickup.address, pickup.externalTMSID),
      toFormStop(1, delivery.address, delivery.externalTMSID),
    ];
  }

  return [
    toFormStop(0, {
      city: suggested?.pickupCity,
      state: suggested?.pickupState,
      zip: suggested?.pickupZip,
    }),
    toFormStop(1, {
      city: suggested?.deliveryCity,
      state: suggested?.deliveryState,
      zip: suggested?.deliveryZip,
    }),
  ];
}

type StopAddressFields = {
  name?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

function toFormStop(
  order: number,
  address: Undef<StopAddressFields>,
  externalTMSID?: string
): QuoteFormStop {
  const city = address?.city?.trim() ?? '';
  const state = address?.state?.trim() ?? '';
  const zip = address?.zip?.trim() ?? '';
  const cityState = city && state ? `${city}, ${state}` : '';

  return {
    order,
    location: zip || cityState,
    city,
    state,
    zip,
    country: (address?.country as QuoteCountries) || QuoteCountries.USA,
    addressName: address?.name ?? '',
    ...(externalTMSID ? { externalTMSID } : {}),
  };
}

function toDate(value: Maybe<Date | string> | undefined): Maybe<Date> {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}
